import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Panel, PanelType } from '@fluentui/react';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  FluentProvider,
  Input,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  Option,
  TagGroup,
  Text,
  makeStyles,
  tokens,
  webLightTheme,
  type TagGroupProps,
} from '@fluentui/react-components';
import { useFetch } from '@moko/ui';
import {
  getCertificationTypes,
  type ICertificationType,
} from '../../services/settingsService';
import type { IUserQualification } from '../../services/userService';
import { dateOnlyToPickerDate, toApiDateOnly } from '../../utils/dateOnly';

interface ICertificationSelectorProps {
  qualifications: IUserQualification[];
  onChange: (qualifications: IUserQualification[]) => void;
  label?: string;
  disabled?: boolean;
}

interface ICertificationForm {
  certificationTypeId: string;
  sourceSystemCode: string;
  externalCredentialId: string;
  isEligible: boolean;
  validUntil: string;
}

const SOURCE_SYSTEM_MAX_LENGTH = 30;
const CREDENTIAL_MAX_LENGTH = 200;

const credentialKeyOf = (qualification: IUserQualification) =>
  `${qualification.sourceSystemCode}|${qualification.externalCredentialId}`.toLowerCase();

const emptyForm: ICertificationForm = {
  certificationTypeId: '',
  sourceSystemCode: 'PORTAL',
  externalCredentialId: '',
  isEligible: false,
  validUntil: '',
};

const useStyles = makeStyles({
  root: {
    width: '100%',
    maxWidth: '500px',
    marginTop: tokens.spacingVerticalL,
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
  },
  tagGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
  emptyText: {
    color: tokens.colorNeutralForeground3,
  },
  panelForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingTop: '8px',
  },
});

const CertificationSelector = ({
  qualifications,
  onChange,
  label = 'Certifications',
  disabled = false,
}: ICertificationSelectorProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const certificationTypesFetch = useFetch<ICertificationType[]>({
    queryKey: ['certificationTypes'],
    queryFn: getCertificationTypes,
  });
  const certificationTypes = (
    Array.isArray(certificationTypesFetch.data)
      ? certificationTypesFetch.data
      : []
  ).filter(
    (item): item is ICertificationType & { id: number } =>
      typeof item.id === 'number'
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ICertificationForm>({
    mode: 'onChange',
    defaultValues: emptyForm,
  });

  useEffect(() => {
    if (isOpen) reset(emptyForm);
  }, [isOpen, reset]);

  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const type of certificationTypes) map.set(type.id, type.name);
    return map;
  }, [certificationTypes]);

  const closePanel = () => setIsOpen(false);

  const addQualification = handleSubmit((values) => {
    const validUntil = toApiDateOnly(values.validUntil);
    onChange([
      ...qualifications,
      {
        certificationTypeId: Number(values.certificationTypeId),
        sourceSystemCode: values.sourceSystemCode.trim(),
        externalCredentialId: values.externalCredentialId.trim(),
        isEligible: values.isEligible,
        validUntil: validUntil ? `${validUntil}T00:00:00Z` : null,
        lastSyncedAt: new Date().toISOString(),
      },
    ]);
    closePanel();
  });

  const dismissQualification: TagGroupProps['onDismiss'] = (_event, data) => {
    onChange(
      qualifications.filter(
        (qualification) => credentialKeyOf(qualification) !== data.value
      )
    );
  };

  return (
    <>
      <Field className={styles.root} label={label} orientation="horizontal">
        <div className={styles.control}>
          {qualifications.length === 0 ? (
            <Text className={styles.emptyText} size={200}>
              No certifications selected
            </Text>
          ) : (
            <TagGroup
              className={styles.tagGroup}
              onDismiss={dismissQualification}
              aria-label={label}
            >
              {qualifications.map((qualification) => (
                <InteractionTag
                  key={credentialKeyOf(qualification)}
                  value={credentialKeyOf(qualification)}
                  appearance="brand"
                >
                  <InteractionTagPrimary
                    hasSecondaryAction={!disabled}
                    secondaryText={qualification.externalCredentialId}
                  >
                    {nameById.get(qualification.certificationTypeId) ??
                      `Certification ${qualification.certificationTypeId}`}
                  </InteractionTagPrimary>
                  {!disabled && <InteractionTagSecondary aria-label="remove" />}
                </InteractionTag>
              ))}
            </TagGroup>
          )}

          <Button
            appearance="secondary"
            size="small"
            onClick={() => setIsOpen(true)}
            disabled={disabled || certificationTypes.length === 0}
          >
            Select certifications
          </Button>
        </div>
      </Field>

      <Panel
        isLightDismiss
        isOpen={isOpen}
        onDismiss={closePanel}
        closeButtonAriaLabel="Close"
        type={PanelType.medium}
        headerText="Add certification"
      >
        <FluentProvider theme={webLightTheme}>
          <form className={styles.panelForm} onSubmit={addQualification}>
            <Controller
              control={control}
              name="certificationTypeId"
              rules={{ required: 'Certification is required.' }}
              render={({ field }) => (
                <Field
                  label="Certification"
                  required
                  validationState={
                    errors.certificationTypeId ? 'error' : 'none'
                  }
                  validationMessage={errors.certificationTypeId?.message}
                >
                  <Dropdown
                    value={nameById.get(Number(field.value)) ?? ''}
                    selectedOptions={field.value ? [field.value] : []}
                    onOptionSelect={(_, data) =>
                      field.onChange(data.optionValue ?? '')
                    }
                  >
                    {certificationTypes.map((type) => (
                      <Option key={type.id} value={String(type.id)}>
                        {type.name}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              )}
            />

            <Controller
              control={control}
              name="externalCredentialId"
              rules={{
                validate: (value, formValues) => {
                  const credentialId = value.trim();
                  if (!credentialId) return 'Credential ID is required.';
                  const key =
                    `${formValues.sourceSystemCode.trim()}|${credentialId}`.toLowerCase();
                  if (
                    qualifications.some(
                      (qualification) => credentialKeyOf(qualification) === key
                    )
                  ) {
                    return 'This credential is already added for this user.';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <Field
                  label="Credential ID"
                  required
                  validationState={
                    errors.externalCredentialId ? 'error' : 'none'
                  }
                  validationMessage={errors.externalCredentialId?.message}
                >
                  <Input
                    value={field.value}
                    maxLength={CREDENTIAL_MAX_LENGTH}
                    onChange={(_, data) => field.onChange(data.value)}
                    onBlur={field.onBlur}
                  />
                </Field>
              )}
            />

            <Controller
              control={control}
              name="sourceSystemCode"
              rules={{
                validate: (value) =>
                  value.trim() !== '' || 'Source system is required.',
              }}
              render={({ field }) => (
                <Field
                  label="Source system"
                  required
                  validationState={errors.sourceSystemCode ? 'error' : 'none'}
                  validationMessage={errors.sourceSystemCode?.message}
                >
                  <Input
                    value={field.value}
                    maxLength={SOURCE_SYSTEM_MAX_LENGTH}
                    onChange={(_, data) => {
                      field.onChange(data.value);
                      void trigger('externalCredentialId');
                    }}
                    onBlur={field.onBlur}
                  />
                </Field>
              )}
            />

            <Controller
              control={control}
              name="validUntil"
              render={({ field }) => (
                <Field label="Valid until">
                  <DatePicker
                    style={{ width: '100%' }}
                    value={dateOnlyToPickerDate(field.value)}
                    onSelectDate={(selected) =>
                      field.onChange(toApiDateOnly(selected))
                    }
                  />
                </Field>
              )}
            />

            <Controller
              control={control}
              name="isEligible"
              render={({ field }) => (
                <Checkbox
                  label="Eligible"
                  checked={field.value}
                  onChange={(_, data) => field.onChange(data.checked === true)}
                />
              )}
            />

            <div>
              <Button appearance="primary" type="submit">
                Add certification
              </Button>
            </div>
          </form>
        </FluentProvider>
      </Panel>
    </>
  );
};

export default CertificationSelector;
